import type { ReactElement } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  getAreaColumn,
  MemberNameCell,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowViewRender } from '@/components/common/RowActions';
import { Checkbox } from '@/components/ui/checkbox';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';
import type { DonationCandidate } from '../types/marketPromotionP2.types';

export const PICK_COLUMN_ID = 'pick';

interface DonationCandidatePicking {
  isPicked: (candidate: DonationCandidate) => boolean;
  onTogglePick: (candidate: DonationCandidate) => void;
  isEveryVisiblePicked: boolean;
  isSomeVisiblePicked: boolean;
  onToggleAllVisible: (shouldPick: boolean) => void;
}

function getTextCell(text: string | null) {
  return text === null || text === '' ? (
    <EmptyValue />
  ) : (
    <WrappingCellText>{text}</WrappingCellText>
  );
}

export function getDonationCandidateColumns(
  picking: DonationCandidatePicking,
  searchText: string | undefined,
  getDonationsLink: (candidate: DonationCandidate) => ReactElement,
): ColumnDef<DonationCandidate, unknown>[] {
  return [
    {
      id: PICK_COLUMN_ID,
      header: () => (
        <Checkbox
          checked={picking.isEveryVisiblePicked}
          indeterminate={picking.isSomeVisiblePicked}
          onCheckedChange={(checked) => picking.onToggleAllVisible(checked === true)}
          aria-label="Pick every ZM shown"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={picking.isPicked(row.original)}
          onCheckedChange={() => picking.onTogglePick(row.original)}
          aria-label={`Pick ${row.original.fullName ?? 'ZM'}`}
        />
      ),
    },
    createSerialColumn<DonationCandidate>(),
    {
      id: 'zonalManager',
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.fullName ?? EMPTY_VALUE,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.mobile,
          }}
          searchText={searchText}
          nameLink={getDonationsLink(row.original)}
        />
      ),
    },
    {
      id: 'designation',
      header: 'Designation',
      cell: ({ row }) => getTextCell(row.original.designation),
    },
    {
      id: 'department',
      header: 'Department',
      cell: ({ row }) => getTextCell(row.original.departmentName),
    },
    getAreaColumn<DonationCandidate>(),
    {
      id: 'donationAmount',
      header: 'Donation Amount',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatNumber(Math.round(row.original.donationAmount))}
        </span>
      ),
    },
    createActionsColumn<DonationCandidate>((candidate) => (
      <RowViewRender
        render={getDonationsLink(candidate)}
        ariaLabel={`Donations of ${candidate.fullName ?? 'this ZM'}`}
      />
    )),
  ];
}
